import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { toast } from "sonner";
import { UserCheck, UserPlus, AlertTriangle, CheckCircle2, Package, Loader2, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal em 2 partes:
 *  A) Dados do cliente (editáveis) + aviso se já existe (por NIF/nome/email)
 *  B) Preview das peças em tabela (com estado matched/not_found)
 * Botão "Confirmar e criar" → chama /purchase-orders/confirm
 *
 * Props:
 *  - open, onClose
 *  - draftId (opcional — se veio de upload PDF)
 *  - initialClient: {name, nif, address, city, postal_code, phone, email, contact_person}
 *  - initialOrderNumber, initialDeliveryDate
 *  - itemsPreview: [{part_code_raw, quantity, description, unit_price, matched_part, match_status}]
 *  - matchedClient: cliente existente encontrado durante extração (pode ser null)
 *  - onConfirmed: callback quando sucesso
 */
export default function PurchaseOrderConfirmDialog({
  open, onClose, draftId,
  initialClient = {}, initialOrderNumber = "", initialDeliveryDate = "",
  itemsPreview = [], matchedClient = null,
  onConfirmed,
}) {
  const [client, setClient] = useState(initialClient);
  const [orderNumber, setOrderNumber] = useState(initialOrderNumber);
  const [deliveryDate, setDeliveryDate] = useState(initialDeliveryDate);
  const [items, setItems] = useState(itemsPreview);
  const [useExistingId, setUseExistingId] = useState(matchedClient?.id || null);
  const [existingMatch, setExistingMatch] = useState(matchedClient);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1=cliente, 2=preview

  // Re-sync when props change (e.g. new upload)
  useEffect(() => {
    setClient(initialClient);
    setOrderNumber(initialOrderNumber);
    setDeliveryDate(initialDeliveryDate);
    setItems(itemsPreview);
    setUseExistingId(matchedClient?.id || null);
    setExistingMatch(matchedClient);
    setStep(1);
  }, [open, initialClient, initialOrderNumber, initialDeliveryDate, itemsPreview, matchedClient]);

  // Lookup em tempo real se editou o NIF
  useEffect(() => {
    const nif = (client.nif || "").trim();
    if (!nif || nif.length < 5) return;
    if (existingMatch && existingMatch.nif === nif) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/clients/lookup?nif=${encodeURIComponent(nif)}`);
        if (r.data.found) {
          setExistingMatch(r.data.matches[0]);
          setUseExistingId(r.data.matches[0].id);
        } else {
          setExistingMatch(null);
          setUseExistingId(null);
        }
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [client.nif]);

  const validPart = items.filter(i => i.matched_part).length;
  const invalidPart = items.length - validPart;

  const submit = async () => {
    if (!client.name?.trim()) { toast.error("Nome do cliente obrigatório"); return; }
    if (validPart === 0) { toast.error("Nenhuma peça válida para criar ordem"); return; }

    const payload = {
      draft_id: draftId || null,
      client_data: {
        name: client.name?.trim(),
        nif: client.nif?.trim() || null,
        address: client.address?.trim() || null,
        city: client.city?.trim() || null,
        postal_code: client.postal_code?.trim() || null,
        phone: client.phone?.trim() || null,
        email: client.email?.trim() || null,
        contact_person: client.contact_person?.trim() || null,
      },
      use_existing_client_id: useExistingId,
      order_number: orderNumber || null,
      delivery_date: deliveryDate || null,
      items: items.filter(i => i.matched_part).map(i => ({
        part_id: i.matched_part.id,
        quantity: parseInt(i.quantity) || 0,
        unit_price: i.unit_price || null,
        description: i.description || null,
      })),
    };
    try {
      setSubmitting(true);
      const r = await axios.post(`${API}/purchase-orders/confirm`, payload);
      const actionLabel = { created: "criado", reused: "reutilizado", updated: "reutilizado (atualizado)" }[r.data.client_action] || "processado";
      toast.success(`${r.data.created_orders.length} ordem(ns) criada(s). Cliente ${actionLabel}.`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} item(ns) com erro`);
      onConfirmed?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a confirmar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 1 ? <UserCheck className="h-5 w-5 text-cyan-600" /> : <Package className="h-5 w-5 text-emerald-600" />}
            Confirmar Encomenda — Passo {step}/2
          </DialogTitle>
          <DialogDescription>
            {step === 1 ? "Confirma os dados do cliente antes de avançar." : "Revê as peças que vão ser encomendadas."}
          </DialogDescription>
        </DialogHeader>

        {/* PASSO 1: CLIENTE */}
        {step === 1 && (
          <div className="space-y-4">
            {existingMatch ? (
              <Alert className="bg-amber-50 border-amber-300">
                <Info className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-900">
                  <b>Cliente já existe:</b> {existingMatch.name} {existingMatch.nif ? `(NIF ${existingMatch.nif})` : ""}.
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <Button size="sm" variant={useExistingId === existingMatch.id ? "default" : "outline"}
                            onClick={() => { setUseExistingId(existingMatch.id); setClient({ ...existingMatch }); }}
                            data-testid="use-existing-btn"
                            className={useExistingId === existingMatch.id ? "bg-amber-600 hover:bg-amber-700" : ""}>
                      <UserCheck className="h-3.5 w-3.5 mr-1" /> Usar este cliente
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setUseExistingId(null)} data-testid="create-new-anyway-btn">
                      <UserPlus className="h-3.5 w-3.5 mr-1" /> Criar novo mesmo assim
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-emerald-50 border-emerald-300">
                <UserPlus className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-900">
                  <b>Novo cliente</b> — vai ser criado automaticamente ao confirmar.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input value={client.name || ""} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="po-client-name" />
              </div>
              <div><Label>NIF</Label><Input value={client.nif || ""} onChange={(e) => setClient({ ...client, nif: e.target.value })} data-testid="po-client-nif" /></div>
              <div><Label>Contacto</Label><Input value={client.contact_person || ""} onChange={(e) => setClient({ ...client, contact_person: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={client.email || ""} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="po-client-email" /></div>
              <div><Label>Telefone</Label><Input value={client.phone || ""} onChange={(e) => setClient({ ...client, phone: e.target.value })} /></div>
              <div className="col-span-2"><Label>Morada</Label><Input value={client.address || ""} onChange={(e) => setClient({ ...client, address: e.target.value })} /></div>
              <div><Label>Localidade</Label><Input value={client.city || ""} onChange={(e) => setClient({ ...client, city: e.target.value })} /></div>
              <div><Label>Código Postal</Label><Input value={client.postal_code || ""} onChange={(e) => setClient({ ...client, postal_code: e.target.value })} /></div>
            </div>

            <div className="border-t pt-3">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Dados da encomenda</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nº Encomenda</Label><Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} data-testid="po-order-number" /></div>
                <div><Label>Data Entrega</Label><Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="po-delivery-date" /></div>
              </div>
            </div>
          </div>
        )}

        {/* PASSO 2: PREVIEW ITEMS */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              <b>{validPart}</b> peça(s) prontas · <span className={invalidPart ? "text-red-600 font-semibold" : ""}>{invalidPart} não encontradas</span>
            </div>
            {invalidPart > 0 && (
              <Alert className="bg-red-50 border-red-300">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">Peças não encontradas serão <b>ignoradas</b>. Cria-as em <i>/parts</i> primeiro se quiseres que sejam encomendadas.</AlertDescription>
              </Alert>
            )}
            <div className="border rounded overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Estado</th>
                    <th className="text-left p-2">Código</th>
                    <th className="text-left p-2">Descrição</th>
                    <th className="text-right p-2">Qtd</th>
                    <th className="text-right p-2">Preço €</th>
                    <th className="text-right p-2">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const subtotal = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0);
                    return (
                      <tr key={idx} className="border-t" data-testid={`po-item-${idx}`}>
                        <td className="p-2">
                          {it.matched_part
                            ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3" /> OK</span>
                            : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3" /> N/D</span>}
                        </td>
                        <td className="p-2 font-mono">{it.matched_part?.code || it.part_code_raw}</td>
                        <td className="p-2 text-xs">{it.matched_part?.description || it.description || <span className="text-slate-400">—</span>}</td>
                        <td className="p-2">
                          <Input type="number" value={it.quantity} onChange={(e) => {
                            const c = [...items]; c[idx] = { ...c[idx], quantity: e.target.value }; setItems(c);
                          }} className="w-24 text-right" data-testid={`po-item-qty-${idx}`} />
                        </td>
                        <td className="p-2">
                          <Input type="number" step="0.01" value={it.unit_price || ""} onChange={(e) => {
                            const c = [...items]; c[idx] = { ...c[idx], unit_price: e.target.value }; setItems(c);
                          }} className="w-24 text-right" placeholder="—" />
                        </td>
                        <td className="p-2 text-right font-mono">{subtotal.toFixed(2)} €</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold">
                  <tr>
                    <td colSpan={5} className="p-2 text-right">TOTAL</td>
                    <td className="p-2 text-right font-mono">
                      {items.reduce((s, it) => s + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0), 0).toFixed(2)} €
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <Button variant="ghost" onClick={onClose} data-testid="po-cancel-btn">Cancelar</Button>
          <div className="flex gap-2">
            {step === 2 && <Button variant="outline" onClick={() => setStep(1)}>← Cliente</Button>}
            {step === 1 && <Button onClick={() => setStep(2)} data-testid="po-next-btn" className="bg-cyan-600 hover:bg-cyan-700">Peças →</Button>}
            {step === 2 && (
              <Button onClick={submit} disabled={submitting || validPart === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="po-confirm-btn">
                {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> A criar…</> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Confirmar e criar ({validPart})</>}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
